import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Play, Pause, Heart, LogOut, User as UserIcon } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { Logo } from './Logo'
import { useOnlinePlayerItems } from '../hooks/useOnlinePlayerItems'

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isMusicPlaying, setIsMusicPlaying] = useState(false)
  const audioRef = useRef<HTMLAudioElement>(null)
  const hoverSoundRef = useRef<HTMLAudioElement>(null)
  const menuOpenSoundRef = useRef<HTMLAudioElement>(null)
  const menuCloseSoundRef = useRef<HTMLAudioElement>(null)
  const { login, logout, user, isAuthenticated } = useOnlinePlayerItems() // Get auth functions from hook - 2025-01-XX
  const [loginData, setLoginData] = useState({
    username: '',
    password: '',
    rememberMe: false
  })
  const [loginError, setLoginError] = useState<string | null>(null) // Login error state - 2025-01-XX
  const [isLoggingIn, setIsLoggingIn] = useState(false) // Loading state for login - 2025-01-XX
  const [lastHoverTime, setLastHoverTime] = useState(0)
  const [buttonPosition, setButtonPosition] = useState({ bottom: 16, right: 16 })
  const location = useLocation()
  
  // Dynamic button positioning to avoid content overlap
  const calculateButtonPosition = () => {
    const viewportHeight = window.innerHeight
    
    // Check for footer content that might be in the bottom right
    const footer = document.querySelector('footer')
    const ctaSection = document.querySelector('section:last-of-type')
    
    // Special handling for home page CTA section
    let homeCtaSection = null
    
    // If we're on the home page, look for the CTA section
    if (location.pathname === '/') {
      const sections = document.querySelectorAll('section')
      for (const section of sections) {
        if (section.textContent?.includes('READY TO BEGIN YOUR ADVENTURE')) {
          homeCtaSection = section
          break
        }
      }
    }
    
    let safeBottom = 16 // Default bottom margin
    
    if (footer) {
      const footerRect = footer.getBoundingClientRect()
      const footerTop = footerRect.top
      
      // If footer is visible and close to bottom, adjust button position
      if (footerTop < viewportHeight && footerTop > viewportHeight - 200) {
        safeBottom = Math.max(16, viewportHeight - footerTop + 16)
      }
    }
    
    // Check CTA section (for home page)
    if (homeCtaSection) {
      const ctaRect = homeCtaSection.getBoundingClientRect()
      const ctaTop = ctaRect.top
      
      // If CTA section is visible, always move buttons up significantly
      if (ctaTop < viewportHeight) {
        // Move buttons well above the CTA section
        safeBottom = Math.max(16, viewportHeight - ctaTop + 80)
      }
    }
    
    
    // Fallback to general section detection
    if (ctaSection && !homeCtaSection) {
      const ctaRect = ctaSection.getBoundingClientRect()
      const ctaBottom = ctaRect.bottom
      
      // If CTA section is close to bottom, adjust button position
      if (ctaBottom > viewportHeight - 100) {
        safeBottom = Math.max(16, viewportHeight - ctaBottom + 16)
      }
    }
    
    setButtonPosition({ bottom: safeBottom, right: 16 })
  }
  
  useEffect(() => {
    calculateButtonPosition()
    
    const handleResize = () => calculateButtonPosition()
    const handleScroll = () => calculateButtonPosition()
    
    window.addEventListener('resize', handleResize)
    window.addEventListener('scroll', handleScroll)
    
    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('scroll', handleScroll)
    }
  }, [location])
  
  // Sound effect functions - 2024-12-19
  const playHoverSound = () => {
    const now = Date.now()
    const cooldownDelay = 200 // 200ms cooldown between hover sounds
    
    // Check if enough time has passed since last hover sound
    if (now - lastHoverTime < cooldownDelay) {
      return // Skip playing sound if too soon
    }
    
    setLastHoverTime(now) // Update the last hover time
    
    if (hoverSoundRef.current) {
      hoverSoundRef.current.currentTime = 0
      hoverSoundRef.current.volume = 0.3
      hoverSoundRef.current.play().catch((error) => {
        console.log('Hover sound failed:', error)
        // Fallback: Create a simple beep sound using Web Audio API
        createBeepSound(800, 0.1, 0.3)
      })
    } else {
      // Fallback: Create a simple beep sound using Web Audio API
      createBeepSound(800, 0.1, 0.3)
    }
  }

  // Fallback beep sound using Web Audio API
  const createBeepSound = (frequency: number, duration: number, volume: number) => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()
      
      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)
      
      oscillator.frequency.value = frequency
      oscillator.type = 'sine'
      
      gainNode.gain.setValueAtTime(0, audioContext.currentTime)
      gainNode.gain.linearRampToValueAtTime(volume, audioContext.currentTime + 0.01)
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + duration)
      
      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + duration)
      
      console.log('Fallback beep sound played')
    } catch (error) {
      console.log('Fallback beep sound failed:', error)
    }
  }

  const playMenuOpenSound = () => {
    if (menuOpenSoundRef.current) {
      menuOpenSoundRef.current.currentTime = 0
      menuOpenSoundRef.current.volume = 0.5
      menuOpenSoundRef.current.play().catch((error) => {
        console.log('Menu open sound failed:', error)
      })
    }
  }

  const playMenuCloseSound = () => {
    if (menuCloseSoundRef.current) {
      menuCloseSoundRef.current.currentTime = 0
      menuCloseSoundRef.current.volume = 0.5
      menuCloseSoundRef.current.play().catch((error) => {
        console.log('Menu close sound failed:', error)
      })
    }
  }

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Server Info', href: '/server-info' },
    // Added Feature Requests link - 2025-01-XX
    { name: 'Feature Requests', href: '/feature-requests' },
    // Added Trading link - 2025-01-XX
    { name: 'Trading', href: '/trading' },
  ]

  const isActive = (path: string) => location.pathname === path

  // Toggle menu with sound effects - 2024-12-19
  const toggleMenu = () => {
    if (isMenuOpen) {
      playMenuCloseSound()
    } else {
      playMenuOpenSound()
    }
    setIsMenuOpen(!isMenuOpen)
  }

  // Setup audio configuration - autoplay disabled - 2025-01-XX
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.loop = true
      audioRef.current.volume = 0.3 // Set volume to 30% for background music
    }
    // Autoplay removed - music must be manually started via toggle button - 2025-01-XX
  }, [])


  const toggleMusic = () => {
    if (audioRef.current) {
      if (isMusicPlaying) {
        audioRef.current.pause()
        setIsMusicPlaying(false)
        console.log('Music paused')
      } else {
        audioRef.current.play()
          .then(() => {
            setIsMusicPlaying(true)
            console.log('Music started')
          })
          .catch((error) => {
            console.log('Music play failed:', error)
          })
      }
    } else {
      console.log('Audio ref not available')
    }
  }

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoginError(null)
    setIsLoggingIn(true)
    
    try {
      // Call login function from hook - 2025-01-XX
      await login(loginData.username, loginData.password)
      // Clear form on success
      setLoginData({
        username: '',
        password: '',
        rememberMe: false
      })
      setIsMenuOpen(false) // Close menu after successful login
    } catch (err: any) {
      setLoginError(err.message || 'Login failed')
    } finally {
      setIsLoggingIn(false)
    }
  }

  const handleLogout = () => {
    logout()
    setIsMenuOpen(false) // Close menu after logout
  }

  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setLoginData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  return (
    <>
      {/* Audio Element for Background Music - 2024-12-19 */}
      <audio
        ref={audioRef}
        src={location.pathname === '/credits' ? "/media/mp3/credits-theme.mp3" : "/media/mp3/Galactic%20Shadows.mp3"}
        preload="auto"
      />
      
      {/* Sound Effects - 2024-12-19 */}
      <audio
        ref={hoverSoundRef}
        preload="auto"
        style={{ display: 'none' }}
      >
        <source src="/media/sfx/hover.mp3" type="audio/mpeg" />
        <source src="/media/sfx/hover.wav" type="audio/wav" />
        <source src="/media/sfx/hover.ogg" type="audio/ogg" />
      </audio>
      <audio
        ref={menuOpenSoundRef}
        preload="auto"
        style={{ display: 'none' }}
      >
        <source src="/media/sfx/menu-open.mp3" type="audio/mpeg" />
        <source src="/media/sfx/menu-open.wav" type="audio/wav" />
        <source src="/media/sfx/menu-open.ogg" type="audio/ogg" />
      </audio>
      <audio
        ref={menuCloseSoundRef}
        preload="auto"
        style={{ display: 'none' }}
      >
        <source src="/media/sfx/menu-close.mp3" type="audio/mpeg" />
        <source src="/media/sfx/menu-close.wav" type="audio/wav" />
        <source src="/media/sfx/menu-close.ogg" type="audio/ogg" />
      </audio>
      
      {/* Logo as Hamburger Menu */}
      <motion.div
        className="fixed top-2 left-2 z-30"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <button
          onClick={toggleMenu}
          onMouseEnter={playHoverSound}
          className="relative transition-all duration-300 hover:scale-105"
        >
          <Logo size="sm" showSubtitle={false} animated={false} />
        </button>
      </motion.div>


      {/* Music Player Button - Dynamic Bottom Right */}
      <motion.div
        className="fixed z-50 transition-all duration-500 ease-in-out"
        style={{
          bottom: `${buttonPosition.bottom}px`,
          right: `${buttonPosition.right}px`
        }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <button
          onClick={toggleMusic}
          onMouseEnter={playHoverSound}
          className={`p-3 bg-slate-800/90 border rounded-full text-gray-300 hover:text-cyan-400 transition-all duration-300 hover:bg-slate-700/90 ${
            isMusicPlaying 
              ? 'border-cyan-400 shadow-cyan-400/50 shadow-lg' 
              : 'border-cyan-500/50 hover:border-cyan-400 hover:shadow-cyan-400/30 hover:shadow-lg'
          }`}
        >
          {isMusicPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
        </button>
      </motion.div>

      {/* Donate Button - Dynamic Bottom Right */}
      <motion.div
        className="fixed z-50 transition-all duration-500 ease-in-out"
        style={{
          bottom: `${buttonPosition.bottom + 60}px`,
          right: `${buttonPosition.right}px`
        }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <button
          onClick={() => {
            // TODO: Implement donate functionality when payment system is ready
            console.log('Donate clicked')
          }}
          onMouseEnter={playHoverSound}
          className="p-3 bg-slate-800/90 border border-cyan-500/50 hover:border-cyan-400 hover:shadow-cyan-400/30 hover:shadow-lg rounded-full text-gray-300 hover:text-cyan-400 transition-all duration-300 hover:bg-slate-700/90"
        >
          <Heart className="w-5 h-5" />
        </button>
      </motion.div>

      {/* Sci-Fi Holographic Terminal */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: -20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="w-64 sm:w-72 bg-black/90 backdrop-blur-xl border border-cyan-400/60 rounded-lg shadow-xl shadow-cyan-400/30 font-mono relative overflow-hidden"
            style={{
              position: 'fixed',
              top: '5rem',
              left: '0.5rem',
              zIndex: 40
            }}
          >
            {/* Terminal Header */}
            <div className="bg-gradient-to-r from-cyan-500/20 to-cyan-500/20 border-b border-cyan-400/40 px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between">
              <span className="text-cyan-300 text-xs sm:text-sm font-bold tracking-wider terminal-cursor">
                NAVIGATION
              </span>
              <div className="text-cyan-300 text-xs font-mono">
                v2.0.1
              </div>
            </div>

            {/* Terminal Content */}
            <div className="p-3 sm:p-4">

              {/* Navigation */}
              <div className="space-y-1 sm:space-y-2">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsMenuOpen(false)}
                    onMouseEnter={playHoverSound}
                    className={`block py-1.5 sm:py-2 px-2 sm:px-3 text-xs sm:text-sm font-mono transition-all duration-300 rounded ${
                      isActive(item.href)
                        ? 'text-cyan-300 bg-cyan-400/20 border-l-2 border-cyan-400'
                        : 'text-gray-300 hover:text-cyan-300 hover:bg-cyan-400/10'
                    }`}
                  >
                    {item.name.toUpperCase()}
                  </Link>
                ))}
              </div>

              {/* Login/User Section - 2025-01-XX */}
              <div className="border-t border-cyan-400/30 pt-3 sm:pt-4 mt-3 sm:mt-4">
                {isAuthenticated && user ? (
                  /* User Info and Logout - 2025-01-XX */
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex items-center gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-cyan-400/10 border border-cyan-400/30 rounded">
                      <UserIcon className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs sm:text-sm font-mono text-cyan-300 truncate">
                          {user.characterName}
                        </div>
                        <div className="text-xs text-gray-400 truncate">
                          {user.username}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={handleLogout}
                      onMouseEnter={playHoverSound}
                      className="w-full flex items-center justify-center gap-2 py-1.5 sm:py-2 px-3 sm:px-4 text-xs sm:text-sm font-mono font-bold text-white bg-red-600 hover:bg-red-500 rounded transition-all duration-300"
                    >
                      <LogOut className="w-3 h-3 sm:w-4 sm:h-4" />
                      LOGOUT
                    </button>
                  </div>
                ) : (
                  /* Login Form - 2025-01-XX */
                  <form onSubmit={handleLoginSubmit} className="space-y-2 sm:space-y-3">
                    {loginError && (
                      <div className="px-2 sm:px-3 py-1.5 text-xs text-red-400 bg-red-900/20 border border-red-500/30 rounded font-mono">
                        {loginError}
                      </div>
                    )}
                    <input
                      name="username"
                      type="text"
                      placeholder="Username"
                      value={loginData.username}
                      onChange={handleLoginChange}
                      onMouseEnter={playHoverSound}
                      className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-cyan-400/40 rounded bg-black/50 text-cyan-300 placeholder-gray-500 focus:outline-none focus:border-cyan-400 font-mono transition-all duration-200"
                      required
                      disabled={isLoggingIn}
                    />
                    <input
                      name="password"
                      type="password"
                      placeholder="Password"
                      value={loginData.password}
                      onChange={handleLoginChange}
                      onMouseEnter={playHoverSound}
                      className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-cyan-400/40 rounded bg-black/50 text-cyan-300 placeholder-gray-500 focus:outline-none focus:border-cyan-400 font-mono transition-all duration-200"
                      required
                      disabled={isLoggingIn}
                    />
                    <button
                      type="submit"
                      onMouseEnter={playHoverSound}
                      disabled={isLoggingIn}
                      className="w-full py-1.5 sm:py-2 px-3 sm:px-4 text-xs sm:text-sm font-mono font-bold text-white bg-cyan-600 hover:bg-cyan-500 rounded transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoggingIn ? 'LOGGING IN...' : 'LOGIN'}
                    </button>
                  </form>
                )}
              </div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backdrop */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-30"
            onClick={() => setIsMenuOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  )
}
